import PropTypes from 'prop-types'
import React, { useState } from 'react'

const formatNumberWithCommas = (number) => {
  if (typeof number !== 'number') return '0'
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const formatNumber = (num) =>
  num !== undefined ? num.toString().padStart(2, '0') : '00'

const CategoriesTableAdminMasterMayor = ({
  counters,
  splitTable,
  expandAllRows,
  pdfViewer,
}) => {
  // Obtener todas las subcategorías de todos los items
  const allSubs = counters?.flatMap((item) => item?.categories || [])
  // Obtener subcategorías únicas por id
  const uniqueSubs = []
  const seen = new Set()
  allSubs?.forEach((sub) => {
    if (!seen.has(sub.id)) {
      seen.add(sub.id)
      uniqueSubs.push(sub)
    }
  })

  // Si splitTable es true, dividir counters en dos partes
  let counters1 = counters
  let counters2 = []
  if (splitTable && counters?.length > 1) {
    const mid = Math.ceil(counters.length / 2)
    counters1 = counters.slice(0, mid)
    counters2 = counters.slice(mid)
  }

  // Estado para controlar qué categorías están desplegadas (por id de subcategoría)
  const [expandedCategories, setExpandedCategories] = useState({})

  const toggleCategory = (catId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [catId]: !prev[catId],
    }))
  }

  // Renderiza una tabla con los counters dados
  const renderTable = (countersPart, tableKey = 0) => (
    <table
      key={tableKey}
      className={`table table-striped table-bordered ${
        countersPart?.length >= 6 ? 'table-block' : ''
      } ${pdfViewer ? 'table-pdf' : ''}`}
    >
      <thead>
        <tr>
          <th
            scope="col"
            colSpan={pdfViewer ? 2 : 1}
            style={pdfViewer ? { minWidth: '200px', width: '30px' } : {}}
          ></th>
          {countersPart?.map((item, index) => (
            <th
              key={`subcategories-${index}`}
              scope="col"
              style={{ minWidth: '100px', borderBottom: '0px' }}
              className="text-center"
              colSpan={2}
            >
              {item?.label}
            </th>
          ))}
        </tr>
        <tr>
          <th
            scope="col"
            rowSpan={2}
            colSpan={pdfViewer ? 2 : 1}
            style={pdfViewer ? { minWidth: '200px' } : {}}
          >
            Categorías
          </th>
          {countersPart?.map((item, index) => (
            <React.Fragment key={`header-cols-${index}`}>
              <th scope="col" colSpan={2} className="text-center">
                {pdfViewer ? 'R/G' : 'Atendidos/Generados'}
              </th>
            </React.Fragment>
          ))}
        </tr>
      </thead>
      <tbody>
        {uniqueSubs.map((sub, idx) => {
          const hasAnySubcategories = countersPart.some((item) => {
            const found = item?.categories?.find((s) => s.id === sub.id)
            return found?.subcategories && found.subcategories.length > 0
          })
          // Handler para expandir/cerrar al hacer click en cualquier celda de la fila
          const handleRowClick = () => {
            if (!expandAllRows && hasAnySubcategories) toggleCategory(sub.id)
          }
          // Determina si la fila está expandida (por prop o por estado)
          const isExpanded = expandAllRows || expandedCategories[sub.id]
          return (
            <React.Fragment key={`subcategory-row-${sub.id}`}>
              <tr
                style={{
                  cursor:
                    hasAnySubcategories && !expandAllRows
                      ? 'pointer'
                      : 'default',
                }}
                onClick={handleRowClick}
                className="category-tr"
              >
                <td
                  className="category-name"
                  colSpan={pdfViewer ? 2 : 1}
                  style={pdfViewer ? { minWidth: '200px' } : {}}
                >
                  <div className="d-flex align-items-left">
                    {/* Mostrar flecha solo si hay subcategorías y expandAllRows es false */}
                    {hasAnySubcategories && !expandAllRows && (
                      <button
                        style={{
                          marginRight: 8,
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          fontSize: 16,
                          padding: 0,
                        }}
                        title={
                          isExpanded
                            ? 'Ocultar subcategorías'
                            : 'Mostrar subcategorías'
                        }
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleCategory(sub.id)
                        }}
                        tabIndex={-1}
                      >
                        {isExpanded ? '▲' : '▼'}
                      </button>
                    )}
                    <div className="d-flex justify-content-center align-items-center">
                      <span
                        style={{
                          display: 'inline-block',
                          backgroundColor: sub?.generatedColor || '#ccc',
                          marginRight: 4,
                          verticalAlign: 'middle',
                        }}
                        title="Generados"
                        className="generated-color"
                      />
                      <span
                        style={{
                          display: 'inline-block',
                          backgroundColor: sub?.inProcessColor || '#ccc',
                          marginRight: 8,
                          verticalAlign: 'middle',
                        }}
                        className="in-process-color"
                        title="En Proceso"
                      />
                    </div>
                    <div style={{ textAlign: 'left' }}>{sub?.name}</div>
                  </div>
                </td>
                {countersPart?.map((item, colIdx) => {
                  const found = item?.categories?.find((s) => s.id === sub.id)
                  const resolved = found?.resolved ?? 0
                  const quantity = found?.quantity ?? 0

                  return (
                    <React.Fragment key={`cols-${colIdx}-${sub.id}`}>
                      <td colSpan={2}>
                        <div>
                          {formatNumber(formatNumberWithCommas(resolved))} /{' '}
                          {formatNumber(formatNumberWithCommas(quantity))}
                        </div>
                      </td>
                    </React.Fragment>
                  )
                })}
              </tr>
              {/* Fila de subcategorías desplegable para toda la fila */}
              {isExpanded &&
                // Por cada subcategoría de cada columna, mostrar una fila por subcategoría (alineada por nombre)
                (() => {
                  // Reunir todas las subcategorías únicas de todas las columnas para esta categoría
                  const allSubcats = []
                  const seenSubcat = new Set()
                  countersPart.forEach((item) => {
                    const found = item?.categories?.find((s) => s.id === sub.id)
                    found?.subcategories?.forEach((subcat) => {
                      if (!seenSubcat.has(subcat.id)) {
                        seenSubcat.add(subcat.id)
                        allSubcats.push(subcat)
                      }
                    })
                  })
                  return allSubcats.map((subcat, subcatIdx) => (
                    <tr
                      key={`subcat-row-${sub.id}-${subcat.id}`}
                      className="subcategory-tr"
                    >
                      <td
                        className="subcategory-name"
                        colSpan={pdfViewer ? 2 : 1}
                        style={pdfViewer ? { minWidth: '200px' } : {}}
                      >
                        <div className="d-flex align-items-left">
                          <div className="subcat" style={{ textAlign: 'left' }}>
                            {subcat.name}
                          </div>
                        </div>
                      </td>
                      {countersPart.map((item, colIdx) => {
                        const found = item?.categories?.find(
                          (s) => s.id === sub.id
                        )
                        const subcatFound = found?.subcategories?.find(
                          (sc) => sc.id === subcat.id
                        )
                        const resolved = subcatFound?.resolved ?? 0
                        const quantity = subcatFound?.quantity ?? 0
                        return (
                          <td
                            colSpan={2}
                            key={`subcat-data-${sub.id}-${colIdx}-${subcat.id}`}
                          >
                            <span>
                              {formatNumber(formatNumberWithCommas(resolved))} /{' '}
                              {formatNumber(formatNumberWithCommas(quantity))}
                            </span>
                          </td>
                        )
                      })}
                    </tr>
                  ))
                })()}
            </React.Fragment>
          )
        })}
      </tbody>
    </table>
  )

  return (
    <div className="sub-categories-table-admin-master-mayor table-responsive">
      {!splitTable && renderTable(counters)}
      {splitTable && (
        <>
          {renderTable(counters1, 1)}
          {counters2.length > 0 && (
            <div style={{ marginTop: 24 }}>{renderTable(counters2, 2)}</div>
          )}
        </>
      )}
    </div>
  )
}

CategoriesTableAdminMasterMayor.propTypes = {
  counters: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      generated: PropTypes.number,
      in_process: PropTypes.number,
      resolved: PropTypes.number,
      effectiveness: PropTypes.number,
      subcategories: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          quantity: PropTypes.number,
          resolved: PropTypes.number,
          generatedColor: PropTypes.string,
          inProcessColor: PropTypes.string,
        })
      ),
    })
  ),
  splitTable: PropTypes.bool,
  expandAllRows: PropTypes.bool, // <-- nuevo parámetro
}

CategoriesTableAdminMasterMayor.defaultProps = {
  counters: [],
  splitTable: false,
  expandAllRows: false, // <-- valor por defecto
}

export default CategoriesTableAdminMasterMayor
